<template>
  <ValidationObserver ref="validator">
    <form ref="form" @submit.prevent="onSubmit">
      <div class="row px-5">
        <div class="col-12 mb-2">
          <Checkbox
            :label="translate('rwUseAgencyAddress')"
            :checked="isFormDirty"
            @changed="changeUserAgentCheckbox"
          />
        </div>

        <div class="col-12 mb-4 mt-2">
          <i class="icon-info-b2b xs"></i>
          {{ translate('rwUseAgencyAddressTooltip') }}
        </div>

        <ValidationObserver
          vid="travelAgentForm"
          :key="render"
          ref="userAgentInfo"
          v-slot="{ dirty }"
        >
          <span>{{ updateValidation(dirty) }}</span>
          <div class="col-12 col-md-6">
            <TextField
              id="streetName"
              name="customerAddress.street.name"
              :label="translate('rwStreetName')"
              :value="data.streetName"
              data-testid="streetName"
            />
          </div>
          <div class="col-12 col-md-6">
            <TextField
              id="streetNumber"
              name="customerAddress.street.number"
              :label="translate('rwStreetNumber')"
              :value="data.streetNumber"
              data-testid="streetNumber"
            />
          </div>
          <div class="col-12 col-md-6">
            <TextField
              id="postalCode"
              name="customerAddress.postalCode"
              :label="translate('rwPostalCode')"
              :value="data.postalCode"
              data-testid="postalCode"
            />
          </div>
          <div class="col-12 col-md-6">
            <TextField
              id="city"
              name="customerAddress.city"
              rules="required"
              :label="translate('rwCity')"
              :value="data.city"
              data-testid="city"
            />
          </div>
          <div class="col-12">
            <SelectField
              v-if="countries.length !== 0"
              id="country"
              name="customerAddress.countryCode"
              rules="required"
              :placeholder="translate('rwCountryOfResidence')"
              :options="countries"
              data-testid="country"
              :value="data.country"
              :autocomplete="true"
            />
          </div>
        </ValidationObserver>
        <DynamicCheckboxes :checkboxes="checkboxes" cssClass="col-12 dynamic-checkbox" />
        <FriendlyRecaptcha
          v-if="isFriendlyCaptchaEnable"
          class="col-12 pt-3"
          location="signup"
          @widget-completed="onFriendlyCaptchaCompleted"
          @widget-error="onFriendlyCaptchaError"
          @widget-expire="onFriendlyCaptchaExpire"
          @widget-reset="onFriendlyCaptchaReset"
          @reset-action="onSetResetAction"
        />
        <div class="col-12 text-center d-flex pt-5">
          <button
            class="btn btn-primary text-uppercase join-submit-btn text-bold"
            :class="loading ? 'm-progress' : ''"
            :disabled="!isSubmitEnabled"
            type="submit"
          >
            {{ translate('rwSubmit') }}
          </button>
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import Checkbox from '@/components/form-controls/Checkbox'
import { ValidationObserver } from 'vee-validate'
import SelectField from '@/components/form-controls/SelectField'
import TextField from '@/components/form-controls/TextField'
import DynamicCheckboxes from '@/components/form-controls/DynamicCheckboxes'
import formJson from 'form-json'
import { loyaltyService } from '../../infrastructure/Factory'
import dynamicCheckBoxes from '@/components/mixins/dynamicCheckBoxes'
import FriendlyCaptchaMixin from '@/components/mixins/FriendlyRecaptcha'
import { mapActions } from 'vuex'
import FriendlyRecaptcha from '@/components/form-controls/FriendlyRecaptcha.vue'
import { consentPiplEnabled } from '@/infrastructure/Configuration'
import { getPageLanguage, getPiplCheckboxLabel } from '@/infrastructure/Utils'

export default {
  name: 'Step2TravelAgent',
  components: {
    ValidationObserver,
    TextField,
    SelectField,
    DynamicCheckboxes,
    Checkbox,
    FriendlyRecaptcha
  },
  mixins: [dynamicCheckBoxes, FriendlyCaptchaMixin],
  props: {
    iata: Object,
    memberType: String,
    loading: {
      type: Boolean,
      default: false
    },
    isChina: Boolean,
    selectedCountry: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      countries: [],
      userAgentCheckbox: false,
      render: false,
      dirty: false,
      checkboxes: [],
      data: {
        country: '',
        defaultInnerText: '',
        city: '',
        postalCode: '',
        streetName: '',
        streetNumber: ''
      }
    }
  },
  computed: {
    isFriendlyCaptchaEnable() {
      return global.ConfigDynamic.isEnabled(global.ConfigDynamicKey.WEB_FRIENDLY_CAPTCHA_B2B)
    },
    isSubmitEnabled () {
      return this.isFriendlyCaptchaEnable ? this.isCaptchaCompleted : true
    },
    isFormDirty() {
      return this.userAgentCheckbox && !this.dirty
    },
    linkPrivacyNotice() {
      return `<button role="button" aria-label="link-privacy-policy" class="btn btn-link btn-link--inline" data-target="#modal-default" data-href="/${getPageLanguage().toLowerCase()}/system/include/modal/privacy-policy#loyalty-programs">${this.translate(
       'peCheckboxPIPLPrivacyNotice'
      )}</button>`
    },
    lblBoCheckboxPIPL() {
      return getPiplCheckboxLabel(this.translate, this.linkPrivacyNotice)
    }
  },
  methods: {
    ...mapActions(['trackErrorEventB2BEnrollment', 'sendStep2Analytics']),
    consentPiplEnabled,
    async onSubmit(event) {
      event.preventDefault()
      const isOk = await this.$refs.validator.validate()

      if (isOk) {
        const json = formJson(event.target)

        if (this.isFriendlyCaptchaEnable) {
          json.cToken = this.captchaToken
        }

        this.$emit('submitTA', { json, callback: this.onResetCaptcha })
      } else {
        this.trackErrorEventB2BEnrollment({ error: this.$refs.validator.errors })
      }
    },
    changeUserAgentCheckbox(event) {
      // false --> true
      if (event) {
        this.fillUserAgentDataFromIata()
        this.$refs.validator.reset()
      } else {
        // true --> false
        this.emptyUserAgentDataFromIata()
      }
      this.userAgentCheckbox = event
    },

    fillUserAgentDataFromIata() {
      this.data.country = this.iata?.countryCode
      this.data.city = this.iata?.city
      this.data.postalCode = this.iata?.postalCode
      this.data.streetName = this.iata?.street?.name
      this.data.streetNumber = this.iata?.street?.number
      this.render = !this.render
    },
    emptyUserAgentDataFromIata() {
      this.data.postalCode = ''
      this.data.streetName = ''
      this.data.streetNumber = ''
      this.data.city = ''
      this.data.country = ''
      this.render = !this.render
    },
    updateValidation(dirty) {
      this.dirty = dirty
    }
  },

  async created() {
    this.userAgentCheckbox = !!this.iata
    this.fillUserAgentDataFromIata()
    this.countries = await loyaltyService.getCountriesByMemberType(this.memberType)
  },

  async mounted() {
    this.sendStep2Analytics()
    this.checkboxes = [
      {
        label: this.lblTermsAndConditions,
        rules: 'checked',
        name: 'customer.termsAndConditions',
        id: 'terms-conditions',
        checked: false,
        dataTestid: 'terms-conditions'
      },
      {
        label: this.lblPrivacyPolicy,
        rules: 'checked',
        name: 'customer.privacyPolicy',
        id: 'privacy-policy',
        checked: false,
        dataTestid: 'privacy-policy'
      },
      {
        label: this.lblMarketingEmailsConsent,
        name: 'customer.marketingEmailsConsent',
        id: 'marketing-emails',
        checked: false,
        dataTestid: 'marketing-emails'
      }
    ]

    if (this.consentPiplEnabled(this.selectedCountry || global.AkamaiService.userCountry())) {
      this.checkboxes.push({
        label: this.lblBoCheckboxPIPL,
        rules: 'checked',
        name: 'customer.consentPIPL',
        id: 'checkboxPIPL',
        checked: false,
        dataTestid: 'checkboxPIPL'
      })
    }
  }
}
</script>

<style lang="scss">
.dynamic-checkbox {
  label.checkbox-field {
    font-size: 1.4rem;
    color: color('dark-grey');
    button.btn.btn-link {
      color: color('black');
      font-weight: bold;
      &:hover {
        color: color('black');
      }
    }
  }
}
</style>
